import { useMemo, useEffect, useCallback, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";

import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { getAutoSelectedPaymentMethod } from "./paymentHelpers";
import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import { EventModel } from "app/models/EventModel";
import { FacilityModel } from "app/models/scheduling/FacilityModel";

interface UseSessionPaymentParams {
  lessonRateOptions: LessonRateModel[];
  coachProfile: CoachProfileModel;
  selectedAthletes: any[];
  availablePackages?: any[];
  setAvailablePackages?: React.Dispatch<React.SetStateAction<any[]>>;
  isEdit?: boolean;
  event?: EventModel;
  watchLessonRateId: any;
  facility?: FacilityModel;
}

export function useSessionPayment({
  lessonRateOptions,
  coachProfile,
  selectedAthletes,
  availablePackages,
  setAvailablePackages,
  isEdit,
  event,
  watchLessonRateId,
  facility,
}: UseSessionPaymentParams) {
  const { watch, setValue, resetField } = useFormContext();

  // CN-1323 (CFTB-3320): packages load async. Until they resolve we don't yet
  // know whether the method will auto-flip to "package", so the payment fields
  // gate on this flag to avoid flashing the "paying parent" note for a family
  // member who actually has a valid package.
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);

  const watchPaymentMethod = watch("payment_method");
  const watchPayingAthlete = watch("paying_athlete_id");
  const watchOnlinePaymentType = watch("online_payment_type");
  const watchSelectedPackageId = watch("selected_package_id");
  const watchChargeTiming = watch("charge_timing");

  const isCustomSession = !watchLessonRateId;
  const paymentEditable = event?.payment_editable !== false;
  const isCheckout = watchOnlinePaymentType === 'checkout';
  const stripeEnabled = !!coachProfile?.allowsPayments;
  const bookingOptionsCapacity = facility?.featureFlags?.bookingOptionsCapacity === true;
  const shouldShowPayingAthlete = selectedAthletes.length > 1 && !bookingOptionsCapacity;

  const sessionDefaultPaymentMethod = useMemo(() => {
    if (watchLessonRateId) {
      const sessionType = lessonRateOptions.find(lr => lr.id === watchLessonRateId);
      if (sessionType?.onlineDefault && stripeEnabled) {
        return 'online';
      }
    }
    return 'offline';
  }, [watchLessonRateId, lessonRateOptions, stripeEnabled]);

  const clearPaymentFields = useCallback(() => {
    resetField("payment_method");
    resetField("paying_athlete_id");
    resetField("selected_package_id");
    resetField("charge_timing");
    setAvailablePackages?.([]);
  }, [resetField, setAvailablePackages]);

  const autoSelectPayment = useCallback(
    (lessonRateId?: string) => {
      const { paymentMethod, selectedPackageId } = getAutoSelectedPaymentMethod({
        lessonRateOptions,
        watchLessonRateId: lessonRateId ?? watchLessonRateId,
        selectedAthletes,
        availablePackages,
        coachProfile,
      });

      if (lessonRateId || watchLessonRateId) {
        setValue("payment_method", paymentMethod, { shouldValidate: true });
        if (paymentMethod === "package" && selectedPackageId) {
          setValue("selected_package_id", selectedPackageId, { shouldValidate: true });
        } else {
          setValue("selected_package_id", null, { shouldValidate: true });
        }
      } else {
        return clearPaymentFields();
      }
    },
    [
      coachProfile,
      setValue,
      selectedAthletes,
      availablePackages,
      lessonRateOptions,
      watchLessonRateId,
      isCustomSession,
      clearPaymentFields,
    ],
  );

  // Clear paying athlete if they're removed from participants (only for new sessions)
  useEffect(() => {
    if (!isEdit && watchPayingAthlete && selectedAthletes.length > 0) {
      const stillSelected = selectedAthletes.some(a => a.id === watchPayingAthlete);
      if (!stillSelected) {
        setValue("paying_athlete_id", null);
      }
    }
  }, [selectedAthletes, watchPayingAthlete, setValue, isEdit]);

  // Auto-select payment when custom session
  useEffect(() => {
    if (isCustomSession) {
      autoSelectPayment();
    }
  }, [isCustomSession]);

  const isChangingSession = useRef(false);
  const initialEditLoad = useRef(isEdit || !!event?.preservePaymentDefaults);

  useEffect(() => {
    isChangingSession.current = true;
  }, [watchLessonRateId]);

  // Default payment method based on session type
  useEffect(() => {
    if (!watchPaymentMethod) {
      let defaultPayment = 'offline';

      if (watchLessonRateId) {
        const sessionType = lessonRateOptions.find(lr => lr.id === watchLessonRateId);
        if (sessionType) {
          defaultPayment = sessionType.onlineDefault && coachProfile?.allowsPayments ? 'online' : 'offline';
          isChangingSession.current = true;
        }
      }

      setValue('payment_method', defaultPayment, { shouldValidate: true });
    }
  }, [watchLessonRateId, watchPaymentMethod, lessonRateOptions, coachProfile, setValue]);

  // Load packages when paying athlete or lesson rate changes
  useEffect(() => {
    let cancelled = false;

    const lessonRateId = watchLessonRateId;
    const payingAthleteId = watchPayingAthlete?.id || watchPayingAthlete;
    const groupAthleteId = !payingAthleteId && bookingOptionsCapacity && selectedAthletes.length > 0
      ? selectedAthletes[0].id
      : null;
    const fetchAthleteId = payingAthleteId || groupAthleteId;

    if (fetchAthleteId && lessonRateId) {
      const coachId = coachProfile?.externalId;
      setIsLoadingPackages(true);

      schedulingFetchGet(
        `/students/${fetchAthleteId}/subscriptions_by_lesson_rate`,
        {
          coach_id: coachId,
          lesson_rate_id: lessonRateId,
        },
      ).then((response) => {
        if (cancelled) { return; }
        const packages = [...(response || [])];

        // CN-2483: a fully-used package is excluded by subscriptions_by_lesson_rate
        // (0 remaining sessions). When editing a session already paid with such a
        // package, re-add it so the coach still sees it selected and can edit the
        // booking. The credit was consumed when the session was booked, so editing
        // it doesn't require another session.
        if (isEdit && event?.payment_method === "package" && event?.selected_package_id) {
          const currentRateLinkId = Number(event.selected_package_id);
          const alreadyListed = packages.some((pkg) => pkg.rateLinkId === currentRateLinkId);
          if (!alreadyListed) {
            packages.push({
              rateLinkId: currentRateLinkId,
              productTitle: event.productName,
              remainingSessions: 0,
              unlimited: false,
            });
          }
        }

        setAvailablePackages(packages);
        setIsLoadingPackages(false);
        const wasChangingSession = isChangingSession.current;
        isChangingSession.current = false;

        if (packages.length > 0) {
          if (initialEditLoad.current) {
            initialEditLoad.current = false;
          } else if ((!isEdit || wasChangingSession) && selectedAthletes.length === 1) {
            setValue("payment_method", "package", { shouldValidate: true });
            setValue("selected_package_id", packages[0]?.rateLinkId, { shouldValidate: true });
          }
        }
      }).catch((err) => {
        if (cancelled) { return; }
        console.error("Failed to load packages:", err);
        setAvailablePackages([]);
        setIsLoadingPackages(false);
        isChangingSession.current = false;
      });
    } else {
      setIsLoadingPackages(false);
      if (!fetchAthleteId) {
        setAvailablePackages([]);
      }
    }

    return () => { cancelled = true; };
  }, [watchPayingAthlete, watchLessonRateId, selectedAthletes, facility]);

  // Auto-select first available package
  useEffect(() => {
    if (watchPaymentMethod === 'package' && availablePackages?.length > 0 && !watchSelectedPackageId) {
      const firstAvailable = availablePackages.find(pkg => pkg.remainingSessions > 0 || pkg.unlimited);
      if (firstAvailable) {
        setValue("selected_package_id", firstAvailable.rateLinkId, { shouldValidate: true });
      }
    }
  }, [watchPaymentMethod, availablePackages, watchSelectedPackageId, setValue]);

  // Default charge timing for online payments
  useEffect(() => {
    if (watchPaymentMethod === 'online' && !watchChargeTiming && !isCheckout) {
      setValue('charge_timing', 'immediate');
    }
    if (watchPaymentMethod !== 'online') {
      setValue('charge_timing', undefined);
    }
  }, [watchPaymentMethod]);

  useEffect(() => {
    if (isEdit && watchPaymentMethod === 'online' && !isCheckout) {
      const originalMethod = event?.payment_method;
      if (originalMethod === 'online') {
        const currentStatus = event?.payment_status;
        if (currentStatus === 'failed' || currentStatus === 'past_due' || currentStatus === 'paid') {
          setValue('payment_status', currentStatus, { shouldValidate: true });
        } else {
          setValue('payment_status', 'unpaid', { shouldValidate: true });
        }
      } else {
        setValue('payment_status', 'unpaid', { shouldValidate: true });
      }
    }
  }, [watchPaymentMethod]);

  return {
    watchPaymentMethod,
    watchPayingAthlete,
    watchOnlinePaymentType,
    watchSelectedPackageId,
    watchChargeTiming,
    isCustomSession,
    paymentEditable,
    isCheckout,
    stripeEnabled,
    sessionDefaultPaymentMethod,
    shouldShowPayingAthlete,
    clearPaymentFields,
    autoSelectPayment,
    isLoadingPackages,
  };
}
